import React from "react";
import { Alert, Spinner } from "@amzn/awsui-components-react";
import { IPermissionBoundaryProps } from "./definitions";
import { useGuestPermissions } from "hooks/useGuestPermissions";

function renderPermissionCheck(
  props: IPermissionBoundaryProps,
): React.ReactElement | null {
  const { isLoading, isError, error, fallbackHeader, fallbackMessage } = props;

  const errorMessage: string = error?.data?.message ?? "";
  const isPncRestricted = errorMessage.includes("Private & Confidential");
  const ownerAlias = errorMessage.match(/Owner:\s*(\S+)/)?.[1];
  const header = isPncRestricted
    ? "Private & Confidential Access Restricted"
    : fallbackHeader || "Access Restricted";
  const message = isPncRestricted
    ? `This resource has been marked as Private & Confidential. Only users explicitly added to the authorized access list can view it. Please contact the audit owner${
        ownerAlias ? ` (${ownerAlias})` : ""
      } to request access.`
    : fallbackMessage || "You are not authorized to access this resource.";

  if (isLoading) {
    return <Spinner />;
  }

  if (
    isError &&
    (error?.status === 403 ||
      (error?.status === 400 &&
        error.data?.__type ===
          "com.amazon.gaiaauditmanagementservice.exceptions#ForbiddenException"))
  ) {
    return (
      <Alert type="warning" header={header}>
        {message}
      </Alert>
    );
  }

  return null;
}

const PermissionBoundary: React.FC<IPermissionBoundaryProps> = (
  props: IPermissionBoundaryProps,
) => {
  const {
    children,
    shouldCheckPermission = true,
    allowMemberOnly = true,
    fallbackMessage,
    fallbackHeader,
  } = props;

  // Do not allow guest to access any Gaia web app pages unless we explicitly to allow
  const { isGuest, teamName } = useGuestPermissions();

  if (!teamName) {
    return <div />;
  }

  if (allowMemberOnly) {
    if (isGuest) {
      return (
        <Alert type="warning" header={fallbackHeader || "Access Restricted"}>
          {fallbackMessage || "You are not authorized to access this page."}
        </Alert>
      );
    }

    if (shouldCheckPermission) {
      const permissionAlert = renderPermissionCheck(props);
      if (permissionAlert) return permissionAlert;
    }

    return <>{children}</>;
  }

  if (shouldCheckPermission) {
    const permissionAlert = renderPermissionCheck(props);
    if (permissionAlert) return permissionAlert;
    return <>{children}</>;
  }

  // this expects children to handle the permission
  return <>{children}</>;
};

export default PermissionBoundary;
